import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useCriticalThinkingItem } from '../lib/api';
import { motion, AnimatePresence, springs, PageTransition, Pressable, FadeIn, Skeleton } from '../lib/motion';
import { haptic } from '../lib/haptics';

const SECTION_LABELS: Record<string, string> = {
  // Case study
  scenario: 'Patient Scenario',
  vitals: 'Vitals',
  history: 'History',
  labs: 'Labs & Imaging',
  medications: 'Medications',
  ncjmmWalkthrough: 'Clinical Judgment Walkthrough',
  recognizeCues: 'Recognize Cues',
  analyzeCues: 'Analyze Cues',
  prioritizeHypotheses: 'Prioritize Hypotheses',
  generateSolutions: 'Generate Solutions',
  takeAction: 'Take Action',
  evaluateOutcomes: 'Evaluate Outcomes',
  decisionPoint: 'Decision Point',
  correctPriority: 'Correct Nursing Priority',
  otherChoicesExplained: 'Why Other Choices Are Less Urgent',
  keyTakeaways: 'Key Takeaways',
  // Pharmacology
  medication: 'Medication',
  drugClass: 'Drug Class',
  indications: 'Indications',
  mechanism: 'Mechanism of Action',
  sideEffects: 'Side Effects',
  contraindications: 'Contraindications',
  labsToMonitor: 'Labs to Monitor',
  patientTeaching: 'Patient Teaching',
  nursingSafety: 'Nursing Safety',
  miniScenario: 'Clinical Scenario',
  rationale: 'Rationale',
  // Stories
  scene: 'Clinical Scene',
  whatNurseNotices: 'What the Nurse Notices',
  whatMatters: 'What Matters',
  whatCouldBeMissed: 'What Could Be Missed',
  firstAction: 'What Should Happen First',
  clinicalReasoning: 'Clinical Reasoning',
  keyTakeaway: 'Key Takeaway',
  triageLevel: 'Triage Level',
  drips: 'Drips & Infusions',
  ventSettings: 'Ventilator Settings',
};

const NCJMM_STEPS = ['recognizeCues', 'analyzeCues', 'prioritizeHypotheses', 'generateSolutions', 'takeAction', 'evaluateOutcomes'];

export function CriticalThinkingDetailPage() {
  const { itemId, discipline = 'nursing' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useCriticalThinkingItem(itemId ?? null);

  useEffect(() => { window.scrollTo(0, 0); }, [itemId]);

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto pb-20">
        <Skeleton className="h-5 w-16 mb-4 rounded-lg" />
        <Skeleton className="h-40 rounded-3xl mb-5" />
        <div className="space-y-3.5">
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      </div>
    );
  }

  const item = data?.item;
  if (!item) {
    return (
      <PageTransition className="text-center py-16">
        <p className="text-slate-500">Item not found.</p>
        <Pressable
          onClick={() => navigate(`/learn/${discipline}`)}
          className="mt-4 text-squishy-primary text-sm font-semibold min-h-[44px] px-4"
        >
          Back to list
        </Pressable>
      </PageTransition>
    );
  }

  const content = item.content ?? {};
  const readMinutes = Math.max(1, Math.round(JSON.stringify(content).split(/\s+/).length / 190));

  return (
    <PageTransition className="max-w-2xl mx-auto pb-20">
      {/* Back button */}
      <Pressable
        onClick={() => navigate(`/learn/${discipline}`)}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-squishy-primary mb-3 transition-colors min-h-[44px] -ml-1 px-1"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        Back
      </Pressable>

      {/* Header */}
      <FadeIn>
        <div className="relative overflow-hidden bg-gradient-to-br from-squishy-dark to-indigo-900 rounded-3xl px-6 py-6 text-white mb-5 shadow-soft">
          <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-squishy-teal/10 blur-3xl pointer-events-none" aria-hidden />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="text-[10px] font-bold bg-white/15 px-2.5 py-1 rounded-full uppercase tracking-wider">
                {item.category.replace(/_/g, ' ')}
              </span>
              <span className="text-[10px] font-medium opacity-50 tracking-wide">{item.externalId}</span>
            </div>
            <h1 className="font-display text-[22px] font-semibold leading-snug">{item.title}</h1>
            <p className="text-sm opacity-70 mt-1.5 leading-relaxed">{item.topic}</p>
            <div className="flex items-center gap-3 mt-4">
              <DifficultyDots difficulty={item.difficulty} />
              {item.clinicalSetting && (
                <span className="text-[11px] opacity-60">{item.clinicalSetting}</span>
              )}
              <span className="text-[11px] opacity-60 ml-auto">{readMinutes} min read</span>
            </div>
          </div>
        </div>
      </FadeIn>

      {/* Content sections */}
      <div className="space-y-3.5">
        {discipline === 'medicine' ? (
          <MedicineContent content={content} />
        ) : discipline === 'law' ? (
          <LawContent content={content} />
        ) : (
          <>
            {item.category === 'case_study' && <CaseStudyContent content={content} />}
            {item.category === 'pharmacology' && <PharmacologyContent content={content} />}
            {(item.category === 'med_surg' || item.category === 'icu' || item.category === 'er') && (
              <StoryContent content={content} category={item.category} />
            )}
          </>
        )}
      </div>

      {/* Source inspiration */}
      {item.sourceInspiration && (
        <div className="mt-5">
          <ExpandableSection title="References" defaultOpen={false}>
            <div className="space-y-2.5">
              {(Array.isArray(item.sourceInspiration) ? item.sourceInspiration : [item.sourceInspiration]).map((src: any, i: number) => (
                <div key={i} className="text-xs leading-5 text-slate-500">
                  <span className="font-semibold text-slate-700">{src.name}</span>
                  {src.used_for && <span className="text-slate-400"> ({src.used_for})</span>}
                  {src.url && (
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block text-squishy-primary truncate hover:underline"
                    >
                      {src.url}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </ExpandableSection>
        </div>
      )}
    </PageTransition>
  );
}

function CaseStudyContent({ content }: { content: Record<string, any> }) {
  return (
    <>
      {content.scenario && <ContentCard title="Patient Scenario" text={content.scenario} accent="blue" />}
      {content.vitals && <VitalsCard vitals={content.vitals} />}
      {content.history && <HistoryCard history={content.history} />}
      {content.labs && <LabsCard labs={content.labs} />}
      {content.medications && <MedicationsCard medications={content.medications} />}
      {content.ncjmmWalkthrough && <NCJMMCard walkthrough={content.ncjmmWalkthrough} />}
      {content.decisionPoint && <ContentCard title="Decision Point" text={content.decisionPoint} accent="amber" />}
      {content.correctPriority && <ContentCard title="Correct Nursing Priority" text={content.correctPriority} accent="emerald" />}
      {content.otherChoicesExplained && <OtherChoicesCard choices={content.otherChoicesExplained} />}
      {content.keyTakeaways && <TakeawaysCard items={content.keyTakeaways} />}
    </>
  );
}

function PharmacologyContent({ content }: { content: Record<string, any> }) {
  return (
    <>
      {content.indications && <ContentCard title="Indications" text={content.indications} accent="purple" />}
      {content.mechanism && <ContentCard title="Mechanism of Action" text={content.mechanism} accent="blue" />}
      {content.sideEffects && <SideEffectsCard sideEffects={content.sideEffects} />}
      {content.contraindications && <ListCard title="Contraindications" items={content.contraindications} accent="red" />}
      {content.labsToMonitor && <ListCard title="Labs to Monitor" items={content.labsToMonitor} accent="amber" />}
      {content.patientTeaching && <ListCard title="Patient Teaching" items={content.patientTeaching} accent="emerald" />}
      {content.nursingSafety && <ContentCard title="Nursing Safety" text={renderValue(content.nursingSafety)} accent="orange" />}
      {content.miniScenario && <ContentCard title="Clinical Scenario" text={content.miniScenario} accent="indigo" />}
      {content.rationale && <ContentCard title="Rationale" text={content.rationale} accent="teal" />}
      {content.keyTakeaways && <TakeawaysCard items={content.keyTakeaways} />}
    </>
  );
}

function StoryContent({ content, category }: { content: Record<string, any>; category: string }) {
  return (
    <>
      {content.triageLevel && (
        <Reveal>
          <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-center gap-2.5">
            <span className="text-red-600 font-bold text-sm">ESI {content.triageLevel.replace(/ESI\s*/i, '')}</span>
            <span className="text-xs font-medium text-red-500 uppercase tracking-wide">Triage Level</span>
          </div>
        </Reveal>
      )}
      {content.scene && <ContentCard title="Clinical Scene" text={content.scene} accent="blue" />}
      {content.vitals && <VitalsCard vitals={content.vitals} />}
      {content.labs && <LabsCard labs={content.labs} />}
      {content.drips && <DripsCard drips={content.drips} />}
      {content.ventSettings && <VentCard settings={content.ventSettings} />}
      {content.whatNurseNotices && <ContentCard title="What the Nurse Notices" text={content.whatNurseNotices} accent="amber" />}
      {content.whatMatters && <ContentCard title="What Matters" text={content.whatMatters} accent="red" />}
      {content.whatCouldBeMissed && <ContentCard title="What Could Be Missed" text={content.whatCouldBeMissed} accent="orange" />}
      {content.firstAction && <ContentCard title="What Should Happen First" text={content.firstAction} accent="emerald" />}
      {content.clinicalReasoning && <ContentCard title="Clinical Reasoning" text={content.clinicalReasoning} accent="indigo" />}
      {content.keyTakeaway && <ContentCard title="Key Takeaway" text={content.keyTakeaway} accent="teal" />}
    </>
  );
}

function MedicineContent({ content }: { content: Record<string, any> }) {
  return (
    <>
      {content.presentation && <ContentCard title="Presentation" text={content.presentation} accent="blue" />}
      {content.scenario && <ContentCard title="Scenario" text={content.scenario} accent="blue" />}
      {content.vitals && <VitalsCard vitals={content.vitals} />}
      {content.exam && <ContentCard title="Physical Exam" text={content.exam} accent="slate" />}
      {content.labs && <LabsCard labs={content.labs} />}
      {content.imaging && <ContentCard title="Imaging & Studies" text={content.imaging} accent="slate" />}
      {content.keyFindings && <ContentCard title="Key Findings" text={content.keyFindings} accent="amber" />}
      {content.immediateAssessment && <ContentCard title="Immediate Assessment (ABCs)" text={content.immediateAssessment} accent="red" />}
      {content.redFlags && <ListCard title="Red Flags" items={content.redFlags} accent="red" />}
      {content.differential && <ListCard title="Differential Diagnosis" items={content.differential} accent="purple" />}
      {content.discriminatingFeatures && <ContentCard title="Discriminating Features" text={content.discriminatingFeatures} accent="indigo" />}
      {content.diagnosticApproach && <ContentCard title="Diagnostic Approach" text={content.diagnosticApproach} accent="blue" />}
      {content.workup && <ContentCard title="Workup" text={content.workup} accent="blue" />}
      {content.testInterpretation && <ContentCard title="Test Interpretation" text={content.testInterpretation} accent="teal" />}
      {/* Pharmacology */}
      {content.medication && <ContentCard title="Medication" text={content.medication} accent="purple" />}
      {content.drugClass && <ContentCard title="Drug Class" text={content.drugClass} accent="purple" />}
      {content.indications && <ContentCard title="Indications" text={content.indications} accent="purple" />}
      {content.mechanism && <ContentCard title="Mechanism of Action" text={content.mechanism} accent="blue" />}
      {content.adverseEffects && <SideEffectsCard sideEffects={content.adverseEffects} />}
      {content.contraindications && <ListCard title="Contraindications" items={content.contraindications} accent="red" />}
      {content.interactions && <ListCard title="Interactions" items={content.interactions} accent="orange" />}
      {content.monitoring && <ListCard title="Monitoring" items={content.monitoring} accent="amber" />}
      {content.patientCounseling && <ListCard title="Patient Counseling" items={content.patientCounseling} accent="emerald" />}
      {content.miniScenario && <ContentCard title="Clinical Scenario" text={content.miniScenario} accent="indigo" />}
      {/* Ethics / EBM */}
      {content.principlesAtPlay && <ListCard title="Principles at Play" items={content.principlesAtPlay} accent="indigo" />}
      {typeof content.analysis === 'string' && <ContentCard title="Analysis" text={content.analysis} accent="blue" />}
      {/* Conclusions */}
      {content.diagnosis && <ContentCard title="Diagnosis" text={content.diagnosis} accent="emerald" />}
      {content.mostLikelyDiagnosis && <ContentCard title="Most Likely Diagnosis" text={content.mostLikelyDiagnosis} accent="emerald" />}
      {content.workupConclusion && <ContentCard title="Conclusion" text={content.workupConclusion} accent="emerald" />}
      {content.management && <ContentCard title="Management" text={content.management} accent="emerald" />}
      {content.firstActions && <ContentCard title="First Actions" text={content.firstActions} accent="emerald" />}
      {content.definitiveManagement && <ContentCard title="Definitive Management" text={content.definitiveManagement} accent="teal" />}
      {content.correctApproach && <ContentCard title="Correct Approach" text={content.correctApproach} accent="emerald" />}
      {content.rationale && <ContentCard title="Rationale" text={content.rationale} accent="teal" />}
      {content.decisionPoint && <ContentCard title="Decision Point" text={content.decisionPoint} accent="amber" />}
      {content.otherChoicesExplained && <OtherChoicesCard choices={content.otherChoicesExplained} />}
      {content.clinicalPearls && <TakeawaysCard items={content.clinicalPearls} />}
      {content.keyTakeaways && <TakeawaysCard items={content.keyTakeaways} />}
    </>
  );
}

function LawContent({ content }: { content: Record<string, any> }) {
  return (
    <>
      {content.citation && <ContentCard title="Citation" text={content.citation} accent="slate" />}
      {content.area && <ContentCard title="Area of Law" text={content.area} accent="slate" />}
      {content.doctrine && <ContentCard title="Doctrine" text={content.doctrine} accent="amber" />}
      {content.facts && <ContentCard title="Facts" text={content.facts} accent="blue" />}
      {content.proceduralPosture && <ContentCard title="Procedural Posture" text={content.proceduralPosture} accent="slate" />}
      {content.hypothetical && <ContentCard title="Hypothetical" text={content.hypothetical} accent="blue" />}
      {content.scenario && <ContentCard title="Scenario" text={content.scenario} accent="blue" />}
      {content.callOfQuestion && <ContentCard title="Call of the Question" text={content.callOfQuestion} accent="amber" />}
      {content.issue && <ContentCard title="Issue" text={content.issue} accent="amber" />}
      {content.issues && <ListCard title="Issues" items={content.issues} accent="amber" />}
      {content.rule && <ContentCard title="Rule" text={content.rule} accent="purple" />}
      {content.elements && <ListCard title="Elements" items={content.elements} accent="blue" />}
      {content.rationale && <ContentCard title="Rationale" text={content.rationale} accent="indigo" />}
      {content.reasoning && <ContentCard title="Reasoning" text={content.reasoning} accent="indigo" />}
      {Array.isArray(content.analysis) && <IracCard steps={content.analysis} />}
      {typeof content.analysis === 'string' && <ContentCard title="Analysis" text={content.analysis} accent="indigo" />}
      {content.application && <ContentCard title="Application" text={content.application} accent="blue" />}
      {content.exceptions && <ListCard title="Exceptions & Limitations" items={content.exceptions} accent="orange" />}
      {content.commonPitfalls && <ListCard title="Common Pitfalls" items={content.commonPitfalls} accent="red" />}
      {content.holding && <ContentCard title="Holding" text={content.holding} accent="emerald" />}
      {content.correctAnswer && <ContentCard title="Correct Answer" text={content.correctAnswer} accent="emerald" />}
      {content.correctAction && <ContentCard title="Correct Action" text={content.correctAction} accent="emerald" />}
      {content.conclusion && <ContentCard title="Conclusion" text={content.conclusion} accent="emerald" />}
      {content.otherChoicesExplained && <OtherChoicesCard choices={content.otherChoicesExplained} />}
      {content.dissent && <ContentCard title="Dissent" text={content.dissent} accent="red" />}
      {content.significance && <ContentCard title="Significance" text={content.significance} accent="teal" />}
      {content.modernApplication && <ContentCard title="Modern Application" text={content.modernApplication} accent="teal" />}
      {content.keyTakeaways && <TakeawaysCard items={content.keyTakeaways} />}
    </>
  );
}

function IracCard({ steps }: { steps: any[] }) {
  const arr = Array.isArray(steps) ? steps : [steps];
  const parts: Array<{ key: string; label: string }> = [
    { key: 'issue', label: 'Issue' },
    { key: 'rule', label: 'Rule' },
    { key: 'application', label: 'Application' },
    { key: 'conclusion', label: 'Conclusion' },
  ];
  return (
    <Reveal>
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/30 dark:bg-indigo-500/10 overflow-hidden">
        <div className="px-5 py-3 bg-indigo-100/50 dark:bg-indigo-500/20 border-b border-indigo-200">
          <h3 className="text-sm font-bold text-indigo-900 dark:text-indigo-200 tracking-tight">IRAC Analysis</h3>
        </div>
        <div className="divide-y divide-indigo-100">
          {arr.map((s: any, i: number) => (
            <div key={i} className="px-5 py-4 space-y-3">
              {parts.map(({ key, label }) => s[key] && (
                <div key={key} className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold flex items-center justify-center mt-0.5" aria-hidden>
                    {label.charAt(0)}
                  </span>
                  <div className="min-w-0">
                    <span className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">{label}</span>
                    <p className="text-[15px] text-slate-700 leading-7 mt-0.5">{renderValue(s[key])}</p>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

// ── Shared cards ──────────────────────────────────────────────────────────────

const ACCENT_STYLES: Record<string, string> = {
  blue: 'border-l-blue-500 bg-blue-50/30 dark:bg-blue-500/10',
  purple: 'border-l-purple-500 bg-purple-50/30 dark:bg-purple-500/10',
  emerald: 'border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-500/10',
  red: 'border-l-red-500 bg-red-50/30 dark:bg-red-500/10',
  amber: 'border-l-amber-500 bg-amber-50/30 dark:bg-amber-500/10',
  orange: 'border-l-orange-500 bg-orange-50/30 dark:bg-orange-500/10',
  indigo: 'border-l-indigo-500 bg-indigo-50/30 dark:bg-indigo-500/10',
  teal: 'border-l-teal-500 bg-teal-50/30 dark:bg-teal-500/10',
  slate: 'border-l-slate-400 bg-slate-50/30 dark:bg-slate-500/10',
};

/** Fades sections in as they scroll into view. Once only; calm and quick. */
function Reveal({ children }: { children: ReactNode }) {
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -40px 0px' }}
      transition={springs.soft}
    >
      {children}
    </motion.div>
  );
}

/** Height-animated collapse for expandable cards. */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <motion.svg
      animate={{ rotate: open ? 180 : 0 }}
      transition={springs.snappy}
      className="w-4 h-4 text-slate-400 shrink-0"
      fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </motion.svg>
  );
}

function ContentCard({ title, text, accent }: { title: string; text: any; accent: string }) {
  const [expanded, setExpanded] = useState(true);
  const style = ACCENT_STYLES[accent] ?? ACCENT_STYLES.slate;
  const displayText = renderValue(text);

  return (
    <Reveal>
      <div className={`rounded-2xl border border-slate-200/80 border-l-4 ${style} overflow-hidden`}>
        <button
          onClick={() => { haptic.light(); setExpanded(!expanded); }}
          aria-expanded={expanded}
          className="w-full flex items-center justify-between gap-3 px-5 py-3 min-h-[44px]"
        >
          <h3 className="text-sm font-bold text-squishy-dark tracking-tight text-left">{title}</h3>
          <Chevron open={expanded} />
        </button>
        <Collapse open={expanded}>
          <div className="px-5 pb-4">
            <div className="text-[15px] text-slate-700 leading-7 whitespace-pre-wrap">{displayText}</div>
          </div>
        </Collapse>
      </div>
    </Reveal>
  );
}

function VitalsCard({ vitals }: { vitals: Record<string, any> }) {
  return (
    <Reveal>
      <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-card">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
          <h3 className="text-sm font-bold text-squishy-dark tracking-tight">Vitals</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-slate-100">
          {Object.entries(vitals).map(([key, val]) => (
            <div key={key} className="bg-white px-4 py-2.5">
              <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{formatKey(key)}</div>
              <div className="text-sm font-semibold text-slate-800 mt-0.5 tabular-nums">{String(val)}</div>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

function HistoryCard({ history }: { history: Record<string, any> }) {
  return (
    <ExpandableSection title="History">
      <div className="space-y-3">
        {Object.entries(history).map(([key, val]) => (
          <div key={key}>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{formatKey(key)}</span>
            <p className="text-[15px] text-slate-700 leading-7 mt-0.5">{renderValue(val)}</p>
          </div>
        ))}
      </div>
    </ExpandableSection>
  );
}

function LabsCard({ labs }: { labs: Record<string, any> }) {
  return (
    <ExpandableSection title="Labs & Imaging">
      <div className="space-y-2">
        {Object.entries(labs).map(([key, val]) => (
          <div key={key} className="flex gap-3">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider shrink-0 w-24 pt-1">{formatKey(key)}</span>
            <span className="text-[15px] text-slate-700 leading-6 tabular-nums">{String(val)}</span>
          </div>
        ))}
      </div>
    </ExpandableSection>
  );
}

function MedicationsCard({ medications }: { medications: any }) {
  const items = Array.isArray(medications) ? medications : [medications];
  return (
    <ExpandableSection title="Medications">
      <ul className="space-y-2">
        {items.map((med: any, i: number) => (
          <li key={i} className="text-[15px] text-slate-700 leading-6 flex gap-2.5">
            <span className="text-squishy-primary font-bold text-xs shrink-0 mt-1">Rx</span>
            <span>{typeof med === 'string' ? med : JSON.stringify(med)}</span>
          </li>
        ))}
      </ul>
    </ExpandableSection>
  );
}

function NCJMMCard({ walkthrough }: { walkthrough: Record<string, any> }) {
  return (
    <Reveal>
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/30 dark:bg-indigo-500/10 overflow-hidden">
        <div className="px-5 py-3 bg-indigo-100/50 dark:bg-indigo-500/20 border-b border-indigo-200">
          <h3 className="text-sm font-bold text-indigo-900 dark:text-indigo-200 tracking-tight">NCJMM Clinical Judgment Walkthrough</h3>
        </div>
        <div className="divide-y divide-indigo-100">
          {NCJMM_STEPS.map((step, i) => {
            const val = walkthrough[step];
            if (!val) return null;
            return (
              <div key={step} className="px-5 py-4 flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold flex items-center justify-center mt-0.5" aria-hidden>
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <span className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">{SECTION_LABELS[step]}</span>
                  <p className="text-[15px] text-slate-700 leading-7 whitespace-pre-wrap mt-1">{val}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Reveal>
  );
}

function SideEffectsCard({ sideEffects }: { sideEffects: any }) {
  if (!sideEffects) return null;
  return (
    <Reveal>
      <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-card">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
          <h3 className="text-sm font-bold text-squishy-dark tracking-tight">Side Effects</h3>
        </div>
        <div className="p-5 space-y-4">
          {sideEffects.common && (
            <div>
              <h4 className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1.5">Common</h4>
              <ul className="space-y-1">
                {sideEffects.common.map((s: string, i: number) => (
                  <li key={i} className="text-[15px] text-slate-700 leading-6 flex gap-2">
                    <span className="text-amber-400 shrink-0" aria-hidden>&bull;</span>{s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {sideEffects.serious && (
            <div>
              <h4 className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1.5">Serious</h4>
              <ul className="space-y-1">
                {sideEffects.serious.map((s: string, i: number) => (
                  <li key={i} className="text-[15px] text-slate-700 leading-6 flex gap-2">
                    <span className="text-red-400 shrink-0" aria-hidden>&bull;</span>{s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Reveal>
  );
}

function ListCard({ title, items, accent }: { title: string; items: any; accent: string }) {
  const arr = Array.isArray(items) ? items : [items];
  const style = ACCENT_STYLES[accent] ?? ACCENT_STYLES.slate;
  return (
    <Reveal>
      <div className={`rounded-2xl border border-slate-200/80 border-l-4 ${style} overflow-hidden`}>
        <div className="px-5 pt-3.5 pb-1">
          <h3 className="text-sm font-bold text-squishy-dark tracking-tight">{title}</h3>
        </div>
        <ul className="px-5 pb-4 pt-1.5 space-y-1.5">
          {arr.map((item: any, i: number) => (
            <li key={i} className="text-[15px] text-slate-700 leading-6 flex gap-2">
              <span className="text-squishy-primary shrink-0" aria-hidden>&bull;</span>
              <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
}

function DripsCard({ drips }: { drips: any }) {
  if (!drips) return null;
  if (typeof drips === 'object' && !Array.isArray(drips)) {
    return (
      <ExpandableSection title="Drips & Infusions">
        <div className="space-y-2">
          {Object.entries(drips).map(([key, val]) => (
            <div key={key} className="flex gap-3">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider shrink-0 w-28 pt-1">{formatKey(key)}</span>
              <span className="text-[15px] text-slate-700 leading-6 tabular-nums">{String(val)}</span>
            </div>
          ))}
        </div>
      </ExpandableSection>
    );
  }
  return null;
}

function VentCard({ settings }: { settings: Record<string, any> }) {
  return (
    <Reveal>
      <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-card">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
          <h3 className="text-sm font-bold text-squishy-dark tracking-tight">Ventilator Settings</h3>
        </div>
        <div className="grid grid-cols-2 gap-px bg-slate-100">
          {Object.entries(settings).map(([key, val]) => (
            <div key={key} className="bg-white px-4 py-2.5">
              <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{formatKey(key)}</div>
              <div className="text-sm font-semibold text-slate-800 mt-0.5 tabular-nums">{String(val)}</div>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

function TakeawaysCard({ items }: { items: any }) {
  const arr = Array.isArray(items) ? items : [items];
  return (
    <Reveal>
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/30 dark:bg-emerald-500/10 overflow-hidden">
        <div className="px-5 py-3 bg-emerald-100/50 dark:bg-emerald-500/20 border-b border-emerald-200">
          <h3 className="text-sm font-bold text-emerald-800 dark:text-emerald-300 tracking-tight">Key Takeaways</h3>
        </div>
        <ul className="px-5 py-4 space-y-2.5">
          {arr.map((item: any, i: number) => (
            <li key={i} className="text-[15px] text-slate-700 leading-6 flex gap-2.5">
              <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-1" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
}

function OtherChoicesCard({ choices }: { choices: any }) {
  const items = Array.isArray(choices) ? choices : [choices];
  return (
    <Reveal>
      <div className="rounded-2xl border border-slate-200/80 border-l-4 border-l-slate-400 bg-slate-50/30 dark:bg-slate-500/10 overflow-hidden">
        <div className="px-5 pt-3.5 pb-1">
          <h3 className="text-sm font-bold text-squishy-dark tracking-tight">Why Other Choices Are Less Urgent</h3>
        </div>
        <div className="px-5 pb-4 pt-1.5 space-y-3.5">
          {items.map((item: any, i: number) => {
            if (typeof item === 'string') {
              return <p key={i} className="text-[15px] text-slate-700 leading-7">{item}</p>;
            }
            return (
              <div key={i} className="border-l-2 border-slate-300 pl-3.5">
                <p className="text-[15px] font-semibold text-slate-800 leading-6">{item.choice ?? item.option ?? `Option ${i + 1}`}</p>
                <p className="text-[15px] text-slate-600 leading-7 mt-0.5">{item.explanation ?? item.rationale ?? renderValue(item)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </Reveal>
  );
}

function ExpandableSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Reveal>
      <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-card">
        <button
          onClick={() => { haptic.light(); setOpen(!open); }}
          aria-expanded={open}
          className="w-full flex items-center justify-between gap-3 px-5 py-3 min-h-[44px]"
        >
          <h3 className="text-sm font-bold text-squishy-dark tracking-tight text-left">{title}</h3>
          <Chevron open={open} />
        </button>
        <Collapse open={open}>
          <div className="px-5 pb-4">{children}</div>
        </Collapse>
      </div>
    </Reveal>
  );
}

function DifficultyDots({ difficulty }: { difficulty: number }) {
  return (
    <div className="flex gap-1 items-center" aria-label={`Difficulty ${difficulty} of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={`w-2 h-2 rounded-full ${i < difficulty ? 'bg-squishy-teal' : 'bg-white/20'}`} />
      ))}
    </div>
  );
}

function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function renderValue(val: any): string {
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map((v) => renderValue(v)).join('\n');
  if (typeof val === 'object' && val !== null) {
    // Handle common structured objects
    if (val.choice && val.explanation) return `${val.choice}: ${val.explanation}`;
    if (val.option && val.rationale) return `${val.option}: ${val.rationale}`;
    if (val.name && val.used_for) return `${val.name} (${val.used_for})`;
    // Fallback: join key-value pairs
    return Object.entries(val).map(([k, v]) => `${formatKey(k)}: ${renderValue(v)}`).join('\n');
  }
  return String(val ?? '');
}
